import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
};

export default function PlayerModal({ track, onClose }) {
  const { title, subtitle, artwork, sources, mediaType = "audio", youtubeId } = track;
  const isVideo = mediaType === "video";
  const isVideoEmbed = isVideo && Boolean(youtubeId);
  const hasArtwork = Boolean(artwork);
  const showControls = !isVideoEmbed;
  const embedUrl = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
    : null;
  const mediaRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (mediaRef.current) mediaRef.current.pause();
    };
  }, []);

  const togglePlay = () => {
    const media = mediaRef.current;
    if (!media) return;
    if (media.paused) {
      const p = media.play();
      if (p?.catch) p.catch(() => {});
    } else {
      media.pause();
    }
  };

  const handleTimeUpdate = () => {
    if (mediaRef.current) setProgress(mediaRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (mediaRef.current) setDuration(mediaRef.current.duration || 0);
  };

  const handleSeek = (e) => {
    const t = Number(e.target.value);
    if (!mediaRef.current || Number.isNaN(t)) return;
    mediaRef.current.currentTime = t;
    setProgress(t);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        className="relative w-full max-w-6xl overflow-hidden rounded-[6px] bg-[#0a0a0c] ring-1 ring-white/8"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 12, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-neutral-300 backdrop-blur-md transition hover:bg-black/60 hover:text-white"
          aria-label="Close player"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>

        <div className="relative w-full h-[70vh] sm:h-[80vh] bg-black">
          {isVideo ? (
            isVideoEmbed ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={embedUrl}
                title={`${title} video`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                ref={mediaRef}
                className="absolute inset-0 w-full h-full object-contain"
                playsInline
                preload="metadata"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              >
                {(sources || []).map((s) => (
                  <source key={s.src} src={s.src} type={s.type} />
                ))}
              </video>
            )
          ) : hasArtwork ? (
            <img
              src={artwork}
              alt={`${title} artwork`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-950" />
          )}

          {showControls && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
          )}

          {showControls && (
            <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6">
              <div className="flex items-center gap-4">
                <motion.button
                  type="button"
                  onClick={togglePlay}
                  aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white/85 transition-colors hover:border-white hover:text-white"
                  whileTap={{ scale: 0.96 }}
                >
                  {isPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="translate-x-[1px]">
                      <path d="M8 5v14l11-7-11-7z" />
                    </svg>
                  )}
                </motion.button>
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  step="0.01"
                  value={progress}
                  onChange={handleSeek}
                  aria-label={`Seek ${title}`}
                  disabled={!duration}
                  className="track-range modal-range flex-1"
                />
              </div>
              <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.14em] text-white/55 tabular-nums">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          )}

          {!isVideo && (
            <audio
              ref={mediaRef}
              preload="metadata"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            >
              {(sources || []).map((s) => (
                <source key={s.src} src={s.src} type={s.type} />
              ))}
            </audio>
          )}
        </div>

        <div className="px-6 sm:px-7 py-5 border-t border-white/8">
          <h3 className="font-sans text-[17px] sm:text-[19px] font-medium tracking-[-0.012em] text-white/95">{title}</h3>
          {subtitle && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
              {subtitle}
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
